import { fetchStorefront } from "./bundle-storefront";
import { cleanDeviceTitle, groupByBrand } from "./device-grouping";

// Fake variant selector rendered by snippets/product-type-switcher.liquid: lists every
// product sharing the current product's type (e.g. one "Custom Phone Case" product per
// device) and navigates to the chosen one. Liquid can't query the catalog by type, so
// the list comes from the Storefront API; the server renders the current product as
// the only option so the row doesn't shift while the list loads.

interface TypeProduct {
  title: string;
  handle: string;
  productType: string;
}

const QUERY_PAGE_SIZE = 250;

class ProductTypeSwitcher extends HTMLElement {
  private select: HTMLSelectElement | null = null;

  connectedCallback(): void {
    this.select = this.querySelector<HTMLSelectElement>("select");
    const productType = this.dataset.productType;
    if (!this.select || !productType || this.dataset.ready) return;
    this.dataset.ready = "true";

    this.loadProducts(productType)
      .then((products) => this.render(products))
      .catch((err) => console.error("[product-type-switcher]", err));
  }

  private async loadProducts(productType: string): Promise<TypeProduct[]> {
    const language = document.documentElement.lang.toUpperCase() === "FR" ? "FR" : "EN";
    const query = `
      query TypeProducts($query: String!, $first: Int!, $after: String) @inContext(language: ${language}) {
        products(first: $first, after: $after, query: $query, sortKey: CREATED_AT, reverse: true) {
          nodes { title handle productType }
          pageInfo { hasNextPage endCursor }
        }
      }
    `;
    const search = `product_type:"${productType.replace(/"/g, '\\"')}"`;

    const products: TypeProduct[] = [];
    let after: string | null = null;
    do {
      const json = await fetchStorefront(query, { query: search, first: QUERY_PAGE_SIZE, after });
      const connection = json?.data?.products;
      if (!connection) throw new Error("Storefront returned no products");
      // The search also matches longer types ("Custom Phone Case - Folio") — keep exact matches only
      products.push(...connection.nodes.filter((p: TypeProduct) => p.productType === productType));
      after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
    } while (after);
    return products;
  }

  private render(products: TypeProduct[]): void {
    const select = this.select!;
    const currentHandle = this.dataset.currentHandle;
    // Keep the server-rendered fallback if the search missed the current product
    // or there is nothing to switch to.
    if (!products.some((p) => p.handle === currentHandle) || products.length < 2) return;

    const langPrefix = document.documentElement.lang.toLowerCase() === "fr" ? "/fr" : "";
    const toOption = (product: TypeProduct): HTMLOptionElement => {
      const option = document.createElement("option");
      option.value = `${langPrefix}/products/${product.handle}`;
      option.textContent = cleanDeviceTitle(product.title);
      option.selected = product.handle === currentHandle;
      return option;
    };

    select.replaceChildren();
    groupByBrand(products).forEach((group) => {
      if (!group.label) {
        group.items.forEach((p) => select.appendChild(toOption(p)));
        return;
      }
      const optgroup = document.createElement("optgroup");
      optgroup.label = group.label;
      group.items.forEach((p) => optgroup.appendChild(toOption(p)));
      select.appendChild(optgroup);
    });

    select.disabled = false;
    new (window as any).Choices(select, { shouldSort: false, shouldSortItems: false, searchEnabled: false });

    select.addEventListener("change", () => {
      if (select.value) window.location.href = select.value;
    });
  }
}

if (!customElements.get("product-type-switcher")) {
  customElements.define("product-type-switcher", ProductTypeSwitcher);
}
